import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Spin, Empty, Button, Segmented } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getFieldIcon } from '../Forecast/icons';
import { WindDirectionIcon } from '../Forecast/WindDirectionIcon';
import { useObservationFields } from '../../hooks/useObservationFields';
import stationObservationsService, { type StationData } from '../../api/stationObservationsService';
import { getEventSource } from '../../utils/eventSource';
import type { EventWithCircuit } from '../../api/eventsService';

dayjs.extend(utc);
dayjs.extend(timezone);

interface MobileStationsObservationsProps {
  event: EventWithCircuit;
  theme: 'light' | 'dark';
}

/** Fetch latest observations - last 60 minutes to now */
function useLatestObservations(event: EventWithCircuit | null) {
  const [stations, setStations] = useState<StationData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!event || event.observations_enabled === false) {
      setStations([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const now = new Date();
      const startTime = new Date(now.getTime() - 60 * 60 * 1000); // 60 min ago

      const source = getEventSource(event);
      const response = await stationObservationsService.getStationObservations(
        event.season_year,
        event.circuit_id,
        event.start_date,
        source,
        startTime.toISOString(),
        now.toISOString()
      );

      setStations(response.stations ?? []);
    } catch (err) {
      console.error('Error fetching mobile station observations:', err);
      setError(err instanceof Error ? err.message : 'Failed to load observations');
      setStations([]);
    } finally {
      setLoading(false);
    }
  }, [event]);

  useEffect(() => {
    fetchData();
    const intervalId = setInterval(fetchData, 60 * 1000);
    return () => clearInterval(intervalId);
  }, [fetchData]);

  return { stations, loading, error, refresh: fetchData };
}

export const MobileStationsObservations: React.FC<MobileStationsObservationsProps> = ({
  event,
  theme,
}) => {
  const { stations, loading, error, refresh } = useLatestObservations(event);
  const { getVisibleFields, getField, createDefaultField } = useObservationFields();
  const [selectedStationIndex, setSelectedStationIndex] = useState(0);

  // Keep selected index in bounds when stations change
  useEffect(() => {
    if (stations.length > 0 && selectedStationIndex >= stations.length) {
      setSelectedStationIndex(0);
    }
  }, [stations, selectedStationIndex]);

  const isDark = theme === 'dark';
  const tz = event.circuit?.time_zone || 'UTC';
  const isDemo = event.demo ?? false;

  // Get latest observation for selected station
  const station = stations[selectedStationIndex] ?? stations[0];
  const latestObs = useMemo(() => {
    if (!station || !station.observations?.length) return null;
    return station.observations.reduce((latest, obs) =>
      new Date(obs.reading_timestamp).getTime() > new Date(latest.reading_timestamp).getTime()
        ? obs
        : latest
    );
  }, [station]);

  // Visible fields for this station
  const visibleFields = useMemo(() => {
    if (!station) return [];
    const availableNames = station.reading_config?.map((c) => c.name) ?? [];
    return getVisibleFields(isDemo)
      .filter((f) => availableNames.includes(f.name))
      .sort((a, b) => (a.position ?? 9999) - (b.position ?? 9999));
  }, [station, isDemo, getVisibleFields]);

  const getReading = (name: string): number | null => {
    if (!station || !latestObs) return null;
    const idx = station.reading_config.findIndex((c) => c.name === name);
    return idx >= 0 ? (latestObs.readings[idx] as number | null) : null;
  };

  const formatValue = (
    fieldName: string,
    value: number | null
  ): string | React.ReactNode => {
    if (value === null) return 'N/A';
    const field = getField(fieldName) ?? createDefaultField(fieldName);

    if (fieldName === 'windDirection') {
      const windDir = latestObs?.car_wind_direction;
      if (windDir === null || windDir === undefined) return 'N/A';
      const windSpeedKmh = getReading('windSpeed') != null ? getReading('windSpeed')! * 3.6 : null;
      return (
        <WindDirectionIcon direction={windDir} windSpeed={windSpeedKmh} size={20} showAngle />
      );
    }
    if (fieldName === 'windSpeed' || fieldName === 'windGust') {
      const kmh = value * 3.6;
      if (fieldName === 'windGust' && kmh <= 0) return 'N/A';
      return `${kmh.toFixed(1)} km/h`;
    }
    const unit = field.unit || '';
    const decimals = unit === '%' || unit === 'lux' ? 0 : 1;
    return `${value.toFixed(decimals)}${unit ? ` ${unit}` : ''}`;
  };

  if (event.observations_enabled === false) {
    return (
      <div
        style={{
          padding: 24,
          textAlign: 'center',
          color: isDark ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.55)',
        }}
      >
        <Empty description="Observations are not available for this event" />
      </div>
    );
  }

  if (loading && stations.length === 0) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200,
          gap: 16,
        }}
      >
        <Spin size="large" />
        <span style={{ color: isDark ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.55)' }}>
          Loading observations…
        </span>
      </div>
    );
  }

  if (error && stations.length === 0) {
    return (
      <div
        style={{
          padding: 24,
          textAlign: 'center',
          color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)',
        }}
      >
        <p style={{ marginBottom: 12 }}>{error}</p>
        <Button icon={<ReloadOutlined />} onClick={refresh}>
          Retry
        </Button>
      </div>
    );
  }

  if (stations.length === 0) {
    return (
      <div style={{ padding: 24 }}>
        <Empty description="No observation data available yet" />
      </div>
    );
  }

  const observationTime = latestObs
    ? dayjs(latestObs.reading_timestamp).tz(tz)
    : null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        padding: 16,
      }}
    >
      {/* Station selector - always visible */}
      <div style={{ flexShrink: 0, marginBottom: 16 }}>
        <Segmented
          value={station?.station_reference}
          options={stations.map((s) => ({
            value: s.station_reference,
            label: `Station ${s.station_reference}`,
          }))}
          onChange={(val) => {
            const idx = stations.findIndex((s) => s.station_reference === val);
            if (idx >= 0) setSelectedStationIndex(idx);
          }}
          block
          size="large"
        />
      </div>

      {/* Timestamp + refresh - always visible */}
      <div
        style={{
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <span
          style={{
            fontSize: 13,
            color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.55)',
          }}
        >
          {observationTime ? `Updated ${observationTime.format('HH:mm')}` : '—'}
        </span>
        <Button type="text" icon={<ReloadOutlined />} onClick={refresh} size="small">
          Refresh
        </Button>
      </div>

      {/* Observation rows - scrollable list */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {visibleFields.map((field) => {
          const value = getReading(field.name);
          return (
            <div
              key={field.name}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                borderRadius: 10,
              }}
            >
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 15,
                  color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)',
                }}
              >
                {getFieldIcon(field.name, { size: 20 })} {field.display_name}
              </span>
              <span
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: isDark ? '#fff' : '#000',
                }}
              >
                {formatValue(field.name, value)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
